import * as THREE from 'three';
import { CONFIG } from '../../config.js';
import { DigitalObject } from '../../rendering/binary/DigitalObject.js';
import { Building } from './Building.js';
import { DigitalSign } from './DigitalSign.js';

/** @typedef {'posX' | 'negX' | 'posZ' | 'negZ'} StreetFace */

/**
 * @typedef {{
 *   name: string;
 *   width: number;
 *   depth: number;
 *   height: number;
 *   shape: 'building' | 'facade' | 'box' | 'pillar';
 *   cellsPerMeter: number;
 *   sign: boolean;
 *   windows: boolean;
 *   entrance: boolean;
 *   rooftop: boolean;
 *   antenna: boolean;
 *   neon: boolean;
 *   silhouetteCap: boolean;
 * }} ArchetypeSpec
 */

/**
 * Binary building volume plus procedural props (signs, windows, roof, entrance).
 */
export class DetailedBuilding {
  /**
   * @param {{
   *   material: import('../../rendering/binary/BinaryMaterial.js').BinaryMaterial;
   *   accentMaterial?: import('../../rendering/binary/BinaryMaterial.js').BinaryMaterial;
   *   centerX: number;
   *   centerZ: number;
   *   spec: ArchetypeSpec;
   *   seed: number;
   *   cellIdBase: number;
   *   streetFace: StreetFace;
   *   signRegistry?: DigitalSign[];
   * }} options
   */
  constructor({
    material,
    accentMaterial = null,
    centerX,
    centerZ,
    spec,
    seed,
    cellIdBase,
    streetFace,
    signRegistry = [],
  }) {
    const entranceMaterial = accentMaterial ?? material;
    this.centerX = centerX;
    this.centerZ = centerZ;
    this.width = spec.width;
    this.depth = spec.depth;
    this.height = spec.height;
    this.signs = [];

    this.group = new THREE.Group();
    this.group.name = spec.name;

    let cellNext = cellIdBase;
    let maxColumns = 1;
    let drawCalls = 0;

    this.body = new Building({
      material,
      centerX,
      centerZ,
      width: spec.width,
      depth: spec.depth,
      height: spec.height,
      cellsPerMeter: spec.cellsPerMeter,
      seed,
      cellIdBase: cellNext,
      shape: spec.shape,
      name: spec.name,
    });
    cellNext += this.body.cellsUsed;
    maxColumns = Math.max(maxColumns, this.body.maxColumns);
    drawCalls += this.body.digital.drawCalls;
    this.group.add(this.body.group);

    const attachDigital = (digital) => {
      cellNext += digital.cellsUsed;
      maxColumns = Math.max(
        maxColumns,
        digital.surfaces.reduce((m, s) => Math.max(m, s.columns), 1),
      );
      drawCalls += digital.drawCalls;
      this.group.add(digital.group);
      return digital;
    };

    if (spec.windows && spec.cellsPerMeter >= 5) {
      const bandH = Math.min(spec.height * 0.32, 7);
      const bandW = Math.min(spec.width * 0.7, 9);
      const pos = this._facadePoint(streetFace, spec, spec.height * 0.4);
      const win = attachDigital(
        new DigitalObject({
          material,
          shape: 'wall',
          size: [bandW, bandH, 0.1],
          position: [pos.x, pos.y, pos.z],
          cellsPerMeter: Math.min(11, spec.cellsPerMeter + 2),
          seed: seed + 101,
          cellIdBase: cellNext,
          name: 'WindowBand',
        }),
      );
      win.group.rotation.y = pos.rotY;
    }

    const showEntrance = spec.entrance || CONFIG.interiors?.enabled;
    if (showEntrance) {
      const pos = this._facadePoint(streetFace, spec, 1.35);
      const door = attachDigital(
        new DigitalObject({
          material: entranceMaterial,
          shape: 'facade',
          size: [Math.min(1.5, spec.width * 0.32), 2.5, 0.45],
          position: [pos.x, pos.y, pos.z],
          cellsPerMeter: Math.max(6, spec.cellsPerMeter),
          seed: seed + 3,
          cellIdBase: cellNext,
          name: 'Entrance',
        }),
      );
      door.group.rotation.y = pos.rotY;
    }

    if (spec.rooftop) {
      const rw = spec.width * 0.42;
      const rd = spec.depth * 0.38;
      const rh = 1.4;
      attachDigital(
        new DigitalObject({
          material,
          shape: 'box',
          size: [rw, rh, rd],
          position: [centerX, spec.height + rh / 2 + 0.08, centerZ],
          cellsPerMeter: Math.max(4, spec.cellsPerMeter - 2),
          seed: seed + 5,
          cellIdBase: cellNext,
          name: 'Rooftop',
        }),
      );
    }

    if (spec.antenna) {
      const ah = 2.8 + (seed % 4) * 0.5;
      attachDigital(
        new DigitalObject({
          material,
          shape: 'pillar',
          size: [0.32, ah, 0.32],
          position: [
            centerX + spec.width * 0.2,
            spec.height + ah / 2 + 0.15,
            centerZ - spec.depth * 0.15,
          ],
          cellsPerMeter: 7,
          seed: seed + 7,
          cellIdBase: cellNext,
          name: 'Antenna',
        }),
      );
    }

    if (spec.silhouetteCap) {
      const ch = 5 + (seed % 3);
      attachDigital(
        new DigitalObject({
          material,
          shape: 'facade',
          size: [spec.width * 0.5, ch, 0.35],
          position: [centerX, spec.height + ch / 2 + 0.1, centerZ],
          cellsPerMeter: 3,
          seed: seed + 11,
          cellIdBase: cellNext,
          name: 'SilhouetteCap',
        }),
      );
    }

    if (spec.sign) {
      const sign = new DigitalSign({
        width: spec.neon ? 2.8 : 2.2,
        height: spec.neon ? 0.75 : 0.6,
        seed: seed + 13,
        neon: spec.neon,
      });
      this._mountSign(sign, spec, streetFace, 0.38);
      this.group.add(sign.mesh);
      this.signs.push(sign);
      signRegistry.push(sign);
    }

    if (spec.neon && spec.width > 6) {
      const neon2 = new DigitalSign({
        width: 1.8,
        height: 0.45,
        seed: seed + 19,
        neon: true,
        phrases: ['SIGNAL DETECTED', '010101010101', 'ACCESS DENIED'],
      });
      this._mountSign(neon2, spec, streetFace, 0.24);
      this.group.add(neon2.mesh);
      this.signs.push(neon2);
      signRegistry.push(neon2);
    }

    if (spec.height >= 14 && spec.cellsPerMeter >= 4) {
      const streams = spec.height > 28 ? 3 : 2;
      for (let s = 0; s < streams; s += 1) {
        const t = (s + 1) / (streams + 1);
        const sh = spec.height * 0.82;
        const pos = this._facadePoint(
          streetFace,
          spec,
          spec.height * (0.12 + t * 0.55),
        );
        const stream = attachDigital(
          new DigitalObject({
            material,
            shape: 'pillar',
            size: [0.28, sh, 0.12],
            position: [pos.x, pos.y + sh / 2, pos.z],
            cellsPerMeter: Math.min(14, spec.cellsPerMeter + 4),
            seed: seed + 400 + s * 17,
            cellIdBase: cellNext,
            name: `FacadeStream_${s}`,
          }),
        );
        stream.group.rotation.y = pos.rotY;
      }
    }

    this.cellsUsed = cellNext - cellIdBase;
    this.maxColumns = maxColumns;
    this.drawCalls = drawCalls;

    /** @type {import('../interiors/BuildingEntranceRegistry.js').BuildingEntranceRecord | null} */
    this.entranceRecord = null;
    if (showEntrance) {
      const door = this._facadePoint(streetFace, spec, 1.35);
      this.entranceRecord = {
        id: '',
        chunkKey: '',
        doorX: door.x,
        doorY: door.y,
        doorZ: door.z,
        doorRotY: door.rotY,
        interiorSeed: seed,
      };
    }
  }

  getCollider() {
    return this.body.getCollider();
  }

  dispose() {
    for (const sign of this.signs) sign.dispose();
    this.body.dispose();
    this.group.clear();
  }

  /**
   * @param {StreetFace} face
   * @param {ArchetypeSpec} spec
   * @param {number} y
   */
  _facadePoint(face, spec, y) {
    const pad = 0.07;
    const hw = spec.width / 2;
    const hd = spec.depth / 2;
    switch (face) {
      case 'posX':
        return {
          x: this.centerX + hw + pad,
          y,
          z: this.centerZ,
          rotY: Math.PI / 2,
        };
      case 'negX':
        return {
          x: this.centerX - hw - pad,
          y,
          z: this.centerZ,
          rotY: -Math.PI / 2,
        };
      case 'posZ':
        return {
          x: this.centerX,
          y,
          z: this.centerZ + hd + pad,
          rotY: 0,
        };
      default:
        return {
          x: this.centerX,
          y,
          z: this.centerZ - hd - pad,
          rotY: Math.PI,
        };
    }
  }

  _mountSign(sign, spec, face, heightFactor) {
    const p = this._facadePoint(face, spec, spec.height * heightFactor);
    sign.mesh.position.set(p.x, p.y, p.z);
    sign.mesh.rotation.y = p.rotY;
  }
}
